import { useState } from "react";
import { Tag, Search, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import CustomerDiscounts from "@/components/CustomerDiscounts";

const STATUS = { Valido: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Riscattato: "bg-slate-500/15 text-slate-300 border-slate-500/30", Scaduto: "bg-red-500/15 text-red-400 border-red-500/30" };

function DiscountResult({ d, busy, onRedeem }) {
  return (
    <div className="rounded-lg border border-border p-4 space-y-2" data-testid="discount-result">
      <div className="flex items-center gap-2">
        <span className="font-mono-code font-bold">{d.code}</span>
        <Badge className={`border ${STATUS[d.status] || ""}`} data-testid="discount-status">{d.status}</Badge>
      </div>
      <p className="text-sm">{d.description}</p>
      <p className="text-xs text-muted-foreground">Campagna: {d.campaign_name} · Cliente: {d.phone} · Scade il {d.expires_at_local}</p>
      {d.redeemed && <p className="text-xs text-muted-foreground">Riscattato il {new Date(d.redeemed_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })} presso {d.redeemed_store_name}</p>}
      {d.status === "Valido" && (
        <Button data-testid="discount-redeem-button" onClick={onRedeem} disabled={busy} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 h-11">
          <CheckCircle2 className="h-4 w-4" /> Riscatta sconto
        </Button>
      )}
    </div>
  );
}

export default function DiscountVerify({ onAuthError, onOpenReceipt }) {
  const [code, setCode] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, fallback, onError) => {
    setBusy(true);
    try {
      setD(await fn());
      return true;
    } catch (e) {
      onError?.();
      if (!onAuthError?.(e)) toast.error(e?.response?.data?.detail || fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const lookup = () => code.trim() && run(() => api.discountLookup(code.trim().toUpperCase()), "Codice non trovato", () => setD(null));
  const redeem = async () => (await run(() => api.discountRedeem(d.code), "Riscatto non riuscito")) && toast.success("Codice sconto riscattato");
  return (
    <Card className="p-5 bg-card border-border space-y-4" data-testid="discount-verify">
      <div className="space-y-1.5">
        <Label className="flex items-center gap-2"><Tag className="h-4 w-4" /> Codice sconto (da campagna WhatsApp)</Label>
        <div className="flex gap-2">
          <Input data-testid="discount-code-input" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder="SC-XXXXXXXX" className="font-mono-code uppercase" />
          <Button data-testid="discount-lookup-button" onClick={lookup} disabled={busy} className="gap-2 shrink-0"><Search className="h-4 w-4" /> Verifica</Button>
        </div>
      </div>
      {d && <DiscountResult d={d} busy={busy} onRedeem={redeem} />}
      <CustomerDiscounts onAuthError={onAuthError} onOpenReceipt={onOpenReceipt} />
    </Card>
  );
}
